/**
 * @fileoverview Time unit arithmetic (MOR-1016)
 * @module timesheets/units
 *
 * Purpose:
 * - One rounding rule for every path that turns time worked into billing
 *   units, so the same seven minutes is never one unit or two depending on how
 *   it was recorded. The direction is firm-configurable (Timesheet Settings →
 *   rounding mode); it defaults to "up".
 * - One definition of the unit bounds the server enforces
 *
 * Before this module the stopwatch rounded up while the calendar drag, the
 * AI drafts and the chat tools rounded to nearest.
 *
 * Pure by design (no ctx, no db) so the client and the server share it. The
 * rounding-mode type is a type-only import, so nothing here depends on Convex
 * at runtime.
 *
 * Used by:
 * - convex/timesheets/agentTools.ts, draftAi.ts, drafting.ts
 * - src/components/timesheets/* (every units input and the timer prefill)
 */

import type { TimeRoundingMode } from "@cvx/timesheets/timesheetSettings/schema";

/** `createEntry` rejects anything outside this range. */
export const MIN_UNITS = 1;
export const MAX_UNITS = 240;

/** How a part unit rounds when the firm has not chosen otherwise. */
export const DEFAULT_ROUNDING_MODE: TimeRoundingMode = "up";

/**
 * Float noise allowance. 12 minutes of 6-minute units must be 2 units even
 * when the division lands on 2.0000000001; a genuine part unit is orders of
 * magnitude larger than this.
 */
const EPSILON = 1e-9;

function clampUnits(units: number): number {
  return Math.min(MAX_UNITS, Math.max(MIN_UNITS, units));
}

/**
 * Round a positive fractional unit count to a whole unit in the firm's
 * chosen direction. The EPSILON nudge keeps a value that is essentially whole
 * (2.0 arriving as 1.9999999999) from rounding to the wrong neighbour.
 */
function applyRounding(value: number, mode: TimeRoundingMode): number {
  switch (mode) {
    case "down":
      return Math.floor(value + EPSILON);
    case "nearest":
      return Math.round(value);
    case "up":
      return Math.ceil(value - EPSILON);
  }
}

/**
 * Whole billing units for a count that may be fractional (a typed "2.5", a
 * model's 3.33), rounded in the firm's direction (default "up"). Deliberately
 * does not clamp: `NaN` for input that is not a positive finite number, and
 * anything over `MAX_UNITS` passes through, so callers keep rejecting bad
 * input rather than silently posting a different figure from the one typed.
 */
export function roundUnits(
  units: number,
  mode: TimeRoundingMode = DEFAULT_ROUNDING_MODE,
): number {
  if (!Number.isFinite(units) || units <= 0) return Number.NaN;
  return applyRounding(units, mode);
}

/**
 * Minutes worked to billing units, rounded in the firm's direction (default
 * "up"), with a floor of one unit: any time recorded at all is at least one
 * unit, even when the firm rounds down.
 */
export function minutesToUnits(
  minutes: number,
  unitMinutes: number,
  mode: TimeRoundingMode = DEFAULT_ROUNDING_MODE,
): number {
  if (!Number.isFinite(minutes) || minutes <= 0) return MIN_UNITS;
  if (!Number.isFinite(unitMinutes) || unitMinutes <= 0) return MIN_UNITS;
  return clampUnits(applyRounding(minutes / unitMinutes, mode));
}
